import React, { createContext, useContext, useState, useEffect } from 'react';

const ReviewsContext = createContext();

export const useReviews = () => {
  const context = useContext(ReviewsContext);
  if (!context) {
    throw new Error('useReviews must be used within a ReviewsProvider');
  }
  return context;
};

const defaultReviews = [
  {
    id: 1,
    cliniqueId: 1,
    cliniqueName: 'Clinique Vétérinaire du Parc',
    author: 'Marie D.',
    email: 'marie.d@email.fr',
    rating: 5,
    date: '12 décembre 2024',
    comment: 'Excellent vétérinaire, très à l\'écoute et doux avec mon chien. Je recommande vivement !',
    animal: 'Chien',
    helpful: 12,
    status: 'approved' // pending, approved, rejected
  },
  {
    id: 2,
    cliniqueId: 1,
    cliniqueName: 'Clinique Vétérinaire du Parc',
    author: 'Pierre M.',
    email: 'pierre.m@email.fr',
    rating: 4,
    date: '8 décembre 2024',
    comment: 'Très professionnel. Le seul bémol est le temps d\'attente un peu long.',
    animal: 'Chat',
    helpful: 5,
    status: 'approved'
  },
  {
    id: 3,
    cliniqueId: 2,
    cliniqueName: 'Vétérinaires Associés Saint-Michel',
    author: 'Sophie L.',
    email: 'sophie.l@email.fr',
    rating: 5,
    date: '1 décembre 2024',
    comment: 'Prend le temps d\'expliquer chaque étape. Mon lapin était entre de bonnes mains.',
    animal: 'NAC',
    helpful: 8,
    status: 'approved'
  },
  {
    id: 4,
    cliniqueId: 3,
    cliniqueName: 'Centre Vétérinaire de Lyon',
    author: 'Jean P.',
    email: 'jean.p@email.fr',
    rating: 3,
    date: '28 novembre 2024',
    comment: 'Consultation correcte mais un peu expéditif à mon goût.',
    animal: 'Chien',
    helpful: 2,
    status: 'pending'
  },
  {
    id: 5,
    cliniqueId: 1,
    cliniqueName: 'Clinique Vétérinaire du Parc',
    author: 'Test User',
    email: 'test@spam.fr',
    rating: 1,
    date: '25 novembre 2024',
    comment: 'Ceci est un spam de test.',
    animal: 'Chat',
    helpful: 0,
    status: 'rejected'
  }
];

export const ReviewsProvider = ({ children }) => {
  const [reviews, setReviews] = useState(() => {
    const saved = localStorage.getItem('vetordv_reviews');
    return saved ? JSON.parse(saved) : defaultReviews;
  });

  useEffect(() => {
    localStorage.setItem('vetordv_reviews', JSON.stringify(reviews));
  }, [reviews]);

  const addReview = (review) => {
    const newReview = {
      ...review,
      id: Date.now(),
      date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
      helpful: 0,
      status: 'pending'
    };
    setReviews(prev => [newReview, ...prev]);
    return newReview;
  };

  const updateReview = (id, updates) => {
    setReviews(prev => prev.map(review => 
      review.id === id ? { ...review, ...updates } : review
    ));
  };

  const deleteReview = (id) => {
    setReviews(prev => prev.filter(review => review.id !== id));
  };

  const approveReview = (id) => {
    updateReview(id, { status: 'approved' });
  };

  const rejectReview = (id) => {
    updateReview(id, { status: 'rejected' });
  };

  const getReviewsByClinic = (cliniqueId) => {
    return reviews.filter(r => r.cliniqueId === cliniqueId && r.status === 'approved');
  };

  const getPendingReviews = () => {
    return reviews.filter(r => r.status === 'pending');
  };

  const getApprovedReviews = () => {
    return reviews.filter(r => r.status === 'approved');
  };

  const getRejectedReviews = () => {
    return reviews.filter(r => r.status === 'rejected');
  };

  const getStats = () => {
    return {
      total: reviews.length,
      pending: reviews.filter(r => r.status === 'pending').length,
      approved: reviews.filter(r => r.status === 'approved').length,
      rejected: reviews.filter(r => r.status === 'rejected').length,
      averageRating: reviews.filter(r => r.status === 'approved').length > 0
        ? (reviews.filter(r => r.status === 'approved').reduce((acc, r) => acc + r.rating, 0) / reviews.filter(r => r.status === 'approved').length).toFixed(1)
        : 0
    };
  };

  const resetToDefault = () => {
    setReviews(defaultReviews);
  };

  return (
    <ReviewsContext.Provider value={{
      reviews,
      addReview,
      updateReview,
      deleteReview,
      approveReview,
      rejectReview,
      getReviewsByClinic,
      getPendingReviews,
      getApprovedReviews,
      getRejectedReviews,
      getStats,
      resetToDefault
    }}>
      {children}
    </ReviewsContext.Provider>
  );
};

export default ReviewsProvider;
